import { useState, useMemo, useCallback, useEffect, useRef } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { api } from '@/services/api';
import { showToast } from '@/contexts/ToastContext';
import { hasValue, hasStringValue } from '@/utils/validation';
import { getCurrentTimeZone } from '@/utils/date-time';
import { DATETIME_FORMAT_SERVER_UTC } from '@/constants/formats';
import { b64toBlob } from '@/utils/blob';
import { isUWPApp, isIOSApp, isAndroidApp } from '@/utils/browser';
import { notifyApp } from '@/services/app-notify';
import { saveAs } from 'file-saver';
import { DateTime } from 'luxon';
import { DateTimePicker } from '@/components/DateTimePicker';
import { Dropdownlist } from '@/components/Dropdownlist';
import { MultiSelect } from '@/components/MultiSelect';

const REPORT_TYPE_LIST = [
  { code: 'annual', value: 'Annual' },
  { code: 'ytd', value: 'Year to Date' },
  { code: 'monthly', value: 'Monthly' },
  { code: 'custom', value: 'Custom' },
];

export function AnalyticsReportsScreen() {
  const { analyticsReportNpsdClientList } = useAuth();

  const [selectedClients, setSelectedClients] = useState<any[]>(() => {
    if (analyticsReportNpsdClientList.length > 0) {
      return [analyticsReportNpsdClientList[0].id];
    }
    return [];
  });
  const [selectedType, setSelectedType] = useState('');
  const [selectedYear, setSelectedYear] = useState('');
  const [selectedMonth, setSelectedMonth] = useState('');
  const [customStartDate, setCustomStartDate] = useState<Date | null>(null);
  const [customEndDate, setCustomEndDate] = useState<Date | null>(null);
  const [generating, setGenerating] = useState(false);
  const [yearList, setYearList] = useState<any[]>([]);
  const [monthList, setMonthList] = useState<any[]>([]);
  const [clientStartDateList, setClientStartDateList] = useState<any[]>([]);
  const [analyticsStartDate, setAnalyticsStartDate] = useState<any>(null);
  const [activePickerKey, setActivePickerKey] = useState<string | null>(null);
  const isInitialMount = useRef(true);

  const onPickerOpenChange = useCallback((key: string, isOpen: boolean) => {
    if (isOpen) setActivePickerKey(key);
    else if (activePickerKey === key) setActivePickerKey(null);
  }, [activePickerKey]);

  // Fetch client start dates on mount
  useEffect(() => {
    api.analyticsReportClientStartDates().then(
      (result) => {
        setClientStartDateList(result);
        // Trigger initial analytics start date update if we have default selection
        if (selectedClients.length > 0 && result.length > 0) {
          updateAnalyticsStartDateFromList(selectedClients, result);
        }
      },
      () => showToast('Error getting start dates.', 'error')
    );
  }, []);

  // Update analytics start date when selected clients change
  const updateAnalyticsStartDateFromList = useCallback((clients: any[], startDates: any[]) => {
    if (hasValue(clients) && clients.length > 0 && hasValue(startDates) && startDates.length > 0) {
      const selectedClientDates = startDates.filter((item: any) => clients.includes(item.id));
      if (selectedClientDates.length === 0) {
        setAnalyticsStartDate(null);
        return;
      }
      const luxonDates = selectedClientDates.map((item: any) => DateTime.fromFormat(item.startDate, DATETIME_FORMAT_SERVER_UTC));
      const minDate = luxonDates.reduce((prev: any, curr: any) =>
        prev.toMillis() < curr.toMillis() ? prev : curr
      );
      setAnalyticsStartDate(minDate);
    } else {
      setAnalyticsStartDate(null);
    }
  }, []);

  // Watch selectedClients → reset selections and update start date
  useEffect(() => {
    if (isInitialMount.current) {
      isInitialMount.current = false;
      return;
    }
    updateAnalyticsStartDateFromList(selectedClients, clientStartDateList);
    setSelectedType('');
    setSelectedYear('');
    setSelectedMonth('');
    setCustomStartDate(null);
    setCustomEndDate(null);
    setMonthList([]);
  }, [selectedClients]);

  // Watch analyticsStartDate → update year options
  useEffect(() => {
    if (!hasValue(analyticsStartDate)) {
      setYearList([]);
      return;
    }
    const minYear: number = analyticsStartDate.year;
    let maxYear: number = DateTime.now().year;
    if (DateTime.now().month === 1) maxYear -= 1;
    const yearData: any[] = [];
    for (let year = maxYear; year >= minYear; year--) {
      yearData.push({ code: year.toString(), value: year.toString() });
    }
    setYearList(yearData);
  }, [analyticsStartDate]);

  // Watch selectedType → reset year, month, custom dates
  useEffect(() => {
    setSelectedYear('');
    setSelectedMonth('');
    setCustomStartDate(null);
    setCustomEndDate(null);
    setMonthList([]);
  }, [selectedType]);

  // Watch selectedYear → reset month and update month options
  useEffect(() => {
    setSelectedMonth('');
    if (!selectedYear || !analyticsStartDate) {
      setMonthList([]);
      return;
    }
    const year = parseInt(selectedYear);
    if (isNaN(year)) { setMonthList([]); return; }

    const minYear: number = analyticsStartDate.year;
    const currentYear: number = DateTime.now().year;
    const monthNames = [1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12].map(
      (m) => DateTime.local(2000, m, 1).monthLong
    );

    let startMonth = 1;
    let endMonth = 12;
    if (year === minYear) startMonth = analyticsStartDate.month;
    if (year === currentYear) endMonth = DateTime.now().month - 1;

    const monthData: any[] = [];
    for (let m = endMonth; m >= startMonth; m--) {
      monthData.push({ code: m.toString(), value: monthNames[m - 1] });
    }
    setMonthList(monthData);
  }, [selectedYear, analyticsStartDate]);

  const isValidForm = useMemo(() => {
    if (!hasValue(selectedClients) || selectedClients.length === 0) return false;
    if (selectedType === 'annual') {
      return hasStringValue(selectedYear);
    } else if (selectedType === 'ytd' || selectedType === 'monthly') {
      return hasStringValue(selectedYear) && hasStringValue(selectedMonth);
    } else if (selectedType === 'custom') {
      return hasValue(customStartDate) && hasValue(customEndDate);
    }
    return false;
  }, [selectedClients, selectedType, selectedYear, selectedMonth, customStartDate, customEndDate]);

  const downloadAnalyticsReport = useCallback(async () => {
    if (!hasValue(selectedClients) || selectedClients.length === 0) {
      showToast('Client selection required.', 'error');
      return;
    }

    let fromDate: any = null;
    let toDate: any = null;

    if (selectedType === 'annual') {
      const y = parseInt(selectedYear);
      fromDate = DateTime.local(y, 1, 1);
      toDate = DateTime.local(y, 12, 31);
    } else if (selectedType === 'ytd' || selectedType === 'monthly') {
      const y = parseInt(selectedYear);
      const m = parseInt(selectedMonth);
      if (selectedType === 'monthly') {
        fromDate = DateTime.local(y, m, 1);
      } else {
        fromDate = DateTime.local(y, 1, 1);
      }
      const daysInMonth = DateTime.local(y, m, 1).daysInMonth;
      toDate = DateTime.local(y, m, daysInMonth!);
    } else if (selectedType === 'custom') {
      fromDate = DateTime.fromJSDate(customStartDate!);
      toDate = DateTime.fromJSDate(customEndDate!);
      if (fromDate > toDate) {
        showToast('End Date must be after Start Date.', 'error');
        return;
      }
    } else {
      showToast('Error: Unknown analytics report type.', 'error');
      return;
    }

    setGenerating(true);
    showToast('Preparing Analytics Report for download. Please wait.', 'success');

    fromDate = fromDate.toUTC();
    toDate = toDate.plus({ hours: 23 }).plus({ minutes: 59 }).plus({ seconds: 59 }).toUTC();

    const data = {
      startDateTime: fromDate.toFormat(DATETIME_FORMAT_SERVER_UTC),
      endDateTime: toDate.toFormat(DATETIME_FORMAT_SERVER_UTC).replace('.000Z', '.999Z'),
      timeZoneId: getCurrentTimeZone(),
      npsdClientIds: selectedClients,
    };

    try {
      const result = await api.getAnalyticsReportPdfBase64(data);
      showToast('Downloaded analytics report.', 'success');
      const pdfBase64String: string = result.pdfBase64;
      const filename: string = result.pdfFilename;
      if (isUWPApp || isIOSApp || isAndroidApp) {
        notifyApp('activityReportPDFBase64', [filename, pdfBase64String]);
      } else {
        const blob = b64toBlob(pdfBase64String, 'application/pdf');
        saveAs(blob, filename);
      }
    } catch {
      showToast('Error downloading analytics report.', 'error');
    }
    setGenerating(false);
  }, [selectedClients, selectedType, selectedYear, selectedMonth, customStartDate, customEndDate]);

  const showTimePeriod = selectedClients.length > 0;
  const showYearMonth = selectedType === 'annual' || selectedType === 'ytd' || selectedType === 'monthly';
  const showMonth = selectedType === 'ytd' || selectedType === 'monthly';
  const showCustomDates = selectedType === 'custom' && selectedClients.length > 0;

  return (
    <div className="kstc-card kst-card-width">
      <div className="kstc-card__body">
        <h5 className="kstc-card__title">Analytics Reports</h5>
        <p className="kstc-card__text">Download Analytics Reports.</p>

        {/* Client Selection */}
        <div className="kstc-row kstc-gy-3">
          <div className="kstc-col-sm-6">
            <label className="kstc-label">Client(s)</label>
            <MultiSelect
              value={selectedClients}
              items={analyticsReportNpsdClientList}
              labelKey="name"
              valueKey="id"
              placeholder="Select Client(s)"
              onChange={setSelectedClients}
            />
          </div>
        </div>

        {/* Time Period */}
        {showTimePeriod && (
          <div className="kstc-row kstc-mt-3 kstc-gy-3">
            <div className="kstc-col-sm-4">
              <label className="kstc-label">Type</label>
              <Dropdownlist value={selectedType} items={REPORT_TYPE_LIST} labelKey="value" valueKey="code" placeholder="Select Type" searchable={false} sortItems={false} onChange={(val) => setSelectedType(val as string)} />
            </div>
            {showYearMonth && (
              <div className="kstc-col-sm-4">
                <label className="kstc-label">Year</label>
                <Dropdownlist value={selectedYear} items={yearList} labelKey="value" valueKey="code" placeholder="Select Year" searchable={false} sortItems={false} onChange={(val) => setSelectedYear(val as string)} />
              </div>
            )}
            {showMonth && (
              <div className="kstc-col-sm-4">
                <label className="kstc-label">Month</label>
                <Dropdownlist value={selectedMonth} items={monthList} labelKey="value" valueKey="code" placeholder="Select Month" searchable={false} sortItems={false} onChange={(val) => setSelectedMonth(val as string)} />
              </div>
            )}
          </div>
        )}

        {/* Custom Date Range */}
        {showCustomDates && (
          <div className="kstc-row kstc-mt-3 kstc-gy-3">
            <div className="kstc-col-sm-6">
              <label className="kstc-label">Start Date</label>
              <DateTimePicker value={customStartDate} type="date" appendToBody={true} onChange={setCustomStartDate} onOpenChange={(val) => onPickerOpenChange('analyticsReportStart', val)} />
            </div>
            <div className="kstc-col-sm-6">
              <label className="kstc-label">End Date</label>
              <DateTimePicker value={customEndDate} type="date" appendToBody={true} onChange={setCustomEndDate} onOpenChange={(val) => onPickerOpenChange('analyticsReportEnd', val)} />
            </div>
          </div>
        )}

        {/* Download Button */}
        {showTimePeriod && (
          <div className="kstc-row kstc-mt-3">
            <div className="kstc-col-sm-12">
              <button
                type="button"
                disabled={!isValidForm || generating}
                onClick={downloadAnalyticsReport}
                className="kstc-btn kstc-btn--primary kstc-btn--lg kstc-float-end"
              >
                {!generating ? (
                  <span>Generate PDF</span>
                ) : (
                  <>
                    <span className="kstc-spinner kstc-spinner--sm" role="status" aria-hidden="true"></span>
                    <span> Generating...</span>
                  </>
                )}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
